/**
 * crypto.js — AES-GCM 256-bit encryption helpers for localStorage token storage.
 *
 * All operations use the native WebCrypto API (crypto.subtle) — no external libraries.
 *
 * Design:
 *  - The CryptoKey is NEVER stored in localStorage or sessionStorage.
 * It lives only in JavaScript heap memory (React Context), fetched fresh
 *    on every authenticated session from the CMS siteSettings endpoint.
 *  - A fresh random 12-byte IV is generated for every encryption call (AES-GCM requirement).
 *  - The stored value in localStorage is a JSON string: { iv, ct, kv }
 *      iv  — Base64-encoded 12-byte initialisation vector
 *      ct  — Base64-encoded ciphertext (includes GCM auth tag)
 *      kv  — key version string (e.g. "v1") for rotation support
 *
 * localStorage key used: 'api_access_token_enc'
 */

const STORAGE_KEY = 'api_access_token_enc';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Convert a Base64 string to a Uint8Array */
const base64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/** Convert a Uint8Array (or ArrayBuffer) to a Base64 string */
const bytesToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
  binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Import a raw 256-bit AES-GCM key from a Base64-encoded string.
 * Returns a CryptoKey object suitable for encrypt/decrypt operations.
 *
 * @param {string} base64Key  — Base64-encoded 32-byte (256-bit) raw key
 * @returns {Promise<CryptoKey>}
 */
export const importKeyFromBase64 = async (base64Key) => {
  const keyBytes = base64ToBytes(base64Key);
  return crypto.subtle.importKey(
'raw',
    keyBytes,
    { name: 'AES-GCM', length: 256 },
    false,         // not extractable — CryptoKey cannot be exported back to bytes
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypt a plaintext string with AES-GCM 256-bit.
 * A fresh random 12-byte IV is generated for every call.
 *
 * @param {CryptoKey} cryptoKey
 * @param {string}    plaintext
 * @returns {Promise<{ iv: string, ct: string }>}  — both values are Base64
 */
export const encryptToken = async (cryptoKey, plaintext) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);

  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    encoded
  );

  return {
    iv: bytesToBase64(iv),
    ct: bytesToBase64(cipherBuffer),
  };
};

/**
 * Decrypt a ciphertext previously produced by encryptToken().
 *
 * @param {CryptoKey} cryptoKey
 * @param {string}    iv   — Base64-encoded IV
 * @param {string}    ct   — Base64-encoded ciphertext
 * @returns {Promise<string>}  — decrypted plaintext
 */
export const decryptToken = async (cryptoKey, iv, ct) => {
  const ivBytes = base64ToBytes(iv);
  const ctBytes = base64ToBytes(ct);

  const plainBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivBytes },
    cryptoKey,
    ctBytes
  );

  return new TextDecoder().decode(plainBuffer);
};

/**
 * Encrypt a token and write it to localStorage under STORAGE_KEY.
 * Overwrites any previously stored value (handles re-call of Create Access Token).
 *
 * @param {CryptoKey} cryptoKey
 * @param {string}    token     — the plaintext access_token
 * @param {string}    keyVersion   — from CMS siteSettings.keyVersion (e.g. "v1")
 */
export const saveEncryptedToken = async (cryptoKey, token, keyVersion) => {
  if (!cryptoKey || !token) return;
  const { iv, ct } = await encryptToken(cryptoKey, token);
  const payload = JSON.stringify({ iv, ct, kv: keyVersion || 'v1' });
  localStorage.setItem(STORAGE_KEY, payload);
};

/**
 * Read the encrypted token from localStorage and decrypt it.
 * Returns null (never throws) if:
 *   - Nothing is stored
 *   - The stored JSON is malformed
 *   - Decryption fails (e.g. after a key rotation — old ciphertext, new key)
 *
 * @param {CryptoKey} cryptoKey
 * @returns {Promise<string|null>}  — decrypted plaintext token, or null
 */
export const loadDecryptedToken = async (cryptoKey) => {
  if (!cryptoKey) return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const { iv, ct } = JSON.parse(raw);
    if (!iv || !ct) return null;
  return await decryptToken(cryptoKey, iv, ct);
  } catch {
    // Covers JSON parse errors, malformed Base64, and GCM auth tag failures
// (which happen when the key has been rotated or the ciphertext tampered with)
    return null;
  }
};

/**
 * Remove the encrypted token from localStorage.
 * Called on logout (in addition to the existing useLogout.js full wipe).
 */
export const clearEncryptedToken = () => {
  localStorage.removeItem(STORAGE_KEY);
};
